/**
 * ToolActivityPage — super-admin view of a single tenant's AI tool calls.
 *
 * Mounted at /super-admin/tenants/:tenantId/tool-activity. Follows the
 * white-label-editor full-page pattern (app shell + sidebar + main-content)
 * because the listing/filtering UI is too dense for the tenant edit modal.
 */

import { useEffect, useMemo, useState } from 'react';
import { endPlacement } from '@/utils/rtl';
import { useLanguage } from '@/hooks/useLanguage';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, DatePicker, Drawer, Select, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { Sidebar } from '@/components/layout/Sidebar';
import toolInvocationService, {
  ToolInvocation,
  TenantToolActivityFilters,
} from '@modules/observability/services/toolInvocationService';

const { RangePicker } = DatePicker;

interface Filters {
  tool_name?: string;
  status?: 'pending' | 'success' | 'error';
  range?: [Dayjs, Dayjs] | null;
}

/**
 * ToolActivityPanel — the tenant's AI tool calls, mountable anywhere.
 *
 * This used to be a whole page behind a button on the workspace's Activity
 * tab: you clicked "Open tool activity", left the console, and came back with
 * the browser's Back. The listing is the activity, so the workspace renders
 * this panel directly and the tab IS the view. The standalone route stays for
 * deep links and simply wraps the same panel in page chrome.
 *
 * It owns no shell of its own — no sidebar, no header, no scroll container —
 * so it drops into the workspace tab or a page without fighting either.
 */
export function ToolActivityPanel({
  tenantId,
  onCount,
}: {
  tenantId: string | undefined;
  /** Reports the total up to the host, which shows it in its own heading. */
  onCount?: (total: number) => void;
}) {
  const { t, direction } = useLanguage();

  const [rows, setRows] = useState<ToolInvocation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [tools, setTools] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({});

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<ToolInvocation | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Pull the distinct tool list once on mount so the filter dropdown has
  // a proper finite set instead of free-text.
  useEffect(() => {
    if (!tenantId) return;
    toolInvocationService
      .listDistinctToolsForTenant(tenantId)
      .then(setTools)
      .catch(() => setTools([]));
  }, [tenantId]);

  // Re-fetch the table whenever pagination or filters change.
  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    const apiFilters: TenantToolActivityFilters = {
      page,
      per_page: perPage,
      tool_name: filters.tool_name,
      status: filters.status,
    };
    if (filters.range && filters.range[0] && filters.range[1]) {
      apiFilters.from = filters.range[0].toISOString();
      apiFilters.to = filters.range[1].toISOString();
    }
    toolInvocationService
      .listForTenant(tenantId, apiFilters)
      .then((res) => {
        setRows(res.invocations);
        setTotal(res.pagination.total);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [tenantId, page, perPage, filters.tool_name, filters.status, filters.range]);

  useEffect(() => { onCount?.(total); }, [total, onCount]);

  const openDetail = async (row: ToolInvocation) => {
    if (!tenantId) return;
    setDrawerOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const full = await toolInvocationService.getDetailForTenant(tenantId, row.invocation_id);
      setDetail(full);
    } catch {
      setDetail(row);  // fallback to the listing payload
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: ColumnsType<ToolInvocation> = useMemo(
    () => [
      {
        title: t('observability.when'),
        dataIndex: 'started_at',
        key: 'started_at',
        width: 200,
        render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
      },
      {
        title: t('insights.tool'),
        dataIndex: 'tool_name',
        key: 'tool_name',
        render: (v: string) => <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>{v}</code>,
      },
      {
        title: t('admin.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (s: string) => (
          <Tag color={s === 'success' ? 'green' : s === 'error' ? 'red' : 'gold'}>{s}</Tag>
        ),
      },
      {
        title: t('observability.latency'),
        dataIndex: 'latency_ms',
        key: 'latency_ms',
        width: 100,
        render: (v: number | null) => (v == null ? '—' : `${v} ms`),
      },
      {
        title: t('legal.matter.conversation'),
        dataIndex: 'conversation_id',
        key: 'conversation_id',
        width: 170,
        render: (v: string | null) =>
          v ? (
            <Link to={`/#/chat?conversation=${v}`} title={v}>
              <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                {v.slice(0, 8)}…
              </code>
            </Link>
          ) : (
            <span style={{ opacity: 0.5 }}>—</span>
          ),
      },
      {
        title: t('admin.user'),
        dataIndex: 'user_id',
        key: 'user_id',
        width: 130,
        render: (v: string | null) =>
          v ? (
            <Tooltip title={v}>
              <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                {v.slice(0, 8)}…
              </code>
            </Tooltip>
          ) : (
            <span style={{ opacity: 0.5 }}>—</span>
          ),
      },
      {
        title: '',
        key: 'open',
        width: 70,
        render: (_: unknown, row: ToolInvocation) => (
          <Button size="small" onClick={() => openDetail(row)} data-testid="tool-activity-open">
            {t('vault.viewFile')}
          </Button>
        ),
      },
    ],
    [tenantId],
  );

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize && pagination.pageSize !== perPage) setPerPage(pagination.pageSize);
  };

  if (!tenantId) {
    return <div style={{ padding: 24 }}>{t('observability.noTenantId')}</div>;
  }

  return (
    <div className="tool-activity-panel" data-testid="tool-activity-panel">
          <div
            style={{
              padding: 'var(--space-4, 16px)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <Select
              placeholder={t('observability.allTools')}
              allowClear
              style={{ minWidth: 200 }}
              value={filters.tool_name}
              onChange={(v) => {
                setFilters((f) => ({ ...f, tool_name: v }));
                setPage(1);
              }}
              options={tools.map((t) => ({ value: t, label: t }))}
              data-testid="tool-activity-filter-tool"
            />
            <Select
              placeholder={t('observability.anyStatus')}
              allowClear
              style={{ minWidth: 150 }}
              value={filters.status}
              onChange={(v) => {
                setFilters((f) => ({ ...f, status: v }));
                setPage(1);
              }}
              options={[
                { value: 'pending', label: 'pending' },
                { value: 'success', label: 'success' },
                { value: 'error', label: 'error' },
              ]}
              data-testid="tool-activity-filter-status"
            />
            <RangePicker
              showTime
              value={filters.range ?? null}
              onChange={(r) => {
                setFilters((f) => ({ ...f, range: (r as [Dayjs, Dayjs]) ?? null }));
                setPage(1);
              }}
            />
            <Button
              onClick={() => {
                setFilters({});
                setPage(1);
              }}
            >
              {t('sidebar.clear')}
            </Button>
          </div>

          <Table<ToolInvocation>
            rowKey="invocation_id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            size="middle"
            pagination={{
              current: page,
              pageSize: perPage,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
            }}
            onChange={handleTableChange}
            style={{ padding: '0 var(--space-4, 16px) var(--space-4, 16px)' }}
          />

      <Drawer
        placement={endPlacement(direction)}
        open={drawerOpen}
        title={detail ? `Tool: ${detail.tool_name}` : 'Invocation'}
        width={620}
        onClose={() => {
          setDrawerOpen(false);
          setDetail(null);
        }}
        data-testid="tool-activity-drawer"
      >
        {detailLoading && <div>Loading…</div>}
        {!detailLoading && detail && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <DetailField label={t('observability.status')}>
              <Tag color={detail.status === 'success' ? 'green' : detail.status === 'error' ? 'red' : 'gold'}>
                {detail.status}
              </Tag>
            </DetailField>
            <DetailField label={t('observability.started')}>
              {detail.started_at ? dayjs(detail.started_at).format('YYYY-MM-DD HH:mm:ss.SSS') : '—'}
            </DetailField>
            <DetailField label={t('observability.latency')}>{detail.latency_ms == null ? '—' : `${detail.latency_ms} ms`}</DetailField>
            <DetailField label={t('observability.invocationId')}>
              <code style={{ fontSize: 12 }}>{detail.invocation_id}</code>
            </DetailField>
            {detail.error_text && (
              <DetailField label={t('observability.error')}>
                <pre
                  style={{
                    background: 'var(--claude-bg-subtle, #fef2f2)',
                    padding: 12,
                    borderRadius: 6,
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {detail.error_text}
                </pre>
              </DetailField>
            )}
            <DetailField label={`Input ${detail.input_truncated ? '(truncated)' : ''}`}>
              <JsonBlock value={detail.input_json} />
            </DetailField>
            <DetailField label={`Output ${detail.output_truncated ? '(truncated)' : ''}`}>
              <JsonBlock value={detail.output_json} />
            </DetailField>
            <DetailField label={t('observability.metadata')}>
              <JsonBlock value={detail.metadata} />
            </DetailField>
          </Space>
        )}
      </Drawer>
    </div>
  );
}

/**
 * ToolActivityPage — the standalone route (/super-admin/tenants/:id/
 * tool-activity). Deep links and bookmarks still work; the workspace's
 * Activity tab renders the panel inline instead.
 */
export default function ToolActivityPage() {
  const { t } = useLanguage();
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [total, setTotal] = useState<number | null>(null);
  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-container">
        <main className="main-content" style={{ overflow: 'auto' }} data-testid="tool-activity-page">
          <div
            className="simple-chat-header"
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            <div>
              <h1 className="bot-title">{t('toolActivity.title')}</h1>
              <p className="text-muted">
                {t('toolActivity.tenant')}{' '}
                <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 'var(--text-xs)' }}>
                  {tenantId}
                </code>
                {/* The count came back from the panel via `onCount`. It was
                    lost when the panel was extracted, and it is the one
                    number this page exists to report. */}
                {total !== null
                  && ` · ${t('toolActivity.invocationsCount', { count: total })}`}
              </p>
            </div>
            <Button onClick={() => navigate(-1)}>{`← ${t('common.back')}`}</Button>
          </div>
          <ToolActivityPanel tenantId={tenantId} onCount={setTotal} />
        </main>
      </div>
    </div>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12, opacity: 0.7 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span style={{ opacity: 0.5 }}>—</span>;
  }
  let text: string;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return (
    <pre
      style={{
        background: 'var(--claude-bg-subtle, #f9fafb)',
        padding: 12,
        borderRadius: 6,
        fontSize: 12,
        maxHeight: 400,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </pre>
  );
}
